import { Button, DatePicker, Empty, Skeleton } from 'antd';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';
import { IoFolderOpenOutline } from 'react-icons/io5';
import { TbFileTypePdf } from 'react-icons/tb';
import { useDispatch, useSelector } from 'react-redux';
import Box from 'src/components/Box';
import RequestItemCollapse from 'src/components/RequestItemCollapse';
import apiRoutes from 'src/utils/apiRoutes';
import apiRequests from 'src/utils/api';
import { RootState } from 'src/store';
import {
  clearRequestsData,
  setRequestsFilters,
} from 'src/store/requests/reducer';
import './index.style.scss';

const { RangePicker } = DatePicker;

interface RequestDetailedData {
  request: Record<string, any>;
  timelogs: Array<{
    uuid: string;
    comment: string;
    date: string;
    minutes: number;
    user_name: string;
    source: string;
    source_title: string;
    billable: boolean;
  }>;
}

const UsagePage: FC = () => {
  const project = useSelector(
    (globalState: RootState) => globalState.projectRetainer.data
  );

  const { filters } = useSelector((globalState: RootState) => globalState.requests);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<RequestDetailedData[]>([]);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [showDetail] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchRequestsDetailed = async (projectUuid: string) => {
      try {
        setLoading(true);
        const params: Record<string, any> = {};

        if (filters.completed_at_start && filters.completed_at_end) {
          params.date_range = [filters.completed_at_start, filters.completed_at_end];
        }

        const response = await apiRequests.get(
          `${apiRoutes.PROJECTS}/${projectUuid}/usage-requests-detailed`,
          params
        );

        if (response.data?.data) {
          setData(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching requests detailed:', error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    if (project?.uuid) {
      fetchRequestsDetailed(project.uuid);
    }
  }, [project?.uuid, filters.completed_at_start, filters.completed_at_end]);

  return project && data ? (
    <>
      <div className="mb-2 flex flex-wrap justify-between gap-2 px-1 text-blue-600">
        <div className="font-bold">
          <IoFolderOpenOutline className="mr-2 inline-block text-2xl" />
          <span className="align-middle">Usage</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Button
              type="link"
              style={{ fontSize: '26px' }}
              icon={<TbFileTypePdf />}
              disabled={(!!data && !data.length) || downloadingPDF}
              onClick={() => {
                setDownloadingPDF(true);

                const fileName =
                  `${project?.customer?.business_name} - ` +
                  `Usage Report - ` +
                  `${
                    filters.completed_at_start && filters.completed_at_end
                      ? `${dayjs(filters.completed_at_start).format(
                          'YYYYMMDD'
                        )}-${dayjs(filters.completed_at_end).format('YYYYMMDD')}`
                      : ''
                  }.pdf`;

                apiRequests
                  .blob(`${apiRoutes.PROJECTS}/${project?.uuid}/usage-requests-detailed`, {
                    format: 'pdf',
                    date_range:
                      filters.completed_at_start && filters.completed_at_end
                        ? [filters.completed_at_start, filters.completed_at_end]
                        : undefined,
                  })
                  .then((response) => {
                    const fileURL = URL.createObjectURL(response.data);
                    const downloadLink = document.createElement('a');
                    downloadLink.href = fileURL;
                    downloadLink.target = '_blank';
                    downloadLink.download = fileName;
                    downloadLink.click();
                    URL.revokeObjectURL(fileURL);
                  })
                  .finally(() => {
                    setDownloadingPDF(false);
                  });
              }}
            />

            <RangePicker
              className="!mr-4"
              dropdownClassName="usage-range-picker--dropdown"
              value={[
                filters.completed_at_start
                  ? dayjs(filters.completed_at_start)
                  : null,
                filters.completed_at_end ? dayjs(filters.completed_at_end) : null,
              ]}
              format="MM/DD/YYYY"
              presets={[
                {
                  label: 'Last 30 days',
                  value: [dayjs().subtract(30, 'd'), dayjs()],
                },
                {
                  label: 'This month',
                  value: [dayjs().startOf('month'), dayjs().endOf('month')],
                },
                {
                  label: 'Last month',
                  value: [
                    dayjs().subtract(1, 'month').startOf('month'),
                    dayjs().subtract(1, 'month').endOf('month'),
                  ],
                },
                {
                  label: 'Last 90 days',
                  value: [dayjs().subtract(90, 'days').startOf('day'), dayjs()],
                },
                {
                  label: 'All',
                  // @ts-ignore
                  value: null,
                },
              ]}
              disabledDate={(value) =>
                value.isBefore(dayjs().subtract(90, 'days').startOf('day'))
              }
              onChange={(dates) => {
                const newDates =
                  dates?.[0] && dates?.[1]
                    ? [
                        dates[0].format('YYYY-MM-DD'),
                        dates[1].format('YYYY-MM-DD'),
                      ]
                    : null;

                dispatch(clearRequestsData());

                dispatch(
                  setRequestsFilters(
                    newDates
                      ? {
                          completed_at_start: newDates[0],
                          completed_at_end: newDates[1],
                        }
                      : null
                  )
                );
              }}
            />
          </div>
        </div>
      </div>

      <div className="mt-2 mb-4 text-left text-xs">
        The hours listed below reflect the time spent during the selected date range.
      </div>

      {/*<div className="mb-4 flex justify-end items-start">*/}
      {/*  <Checkbox*/}
      {/*      checked={showDetail}*/}
      {/*      onChange={(e) => setShowDetail(e.target.checked)}*/}
      {/*      className="flex-none"*/}
      {/*  >*/}
      {/*    <span className="text-sm text-blue-600">Show detail</span>*/}
      {/*  </Checkbox>*/}
      {/*</div>*/}

      <div>
        {loading && !data?.length ? (
          <Skeleton className="py-4" />
        ) : (
          <div className="space-y-3">
            {data?.map((item) => {
              return (
                <RequestItemCollapse
                  key={item.request.uuid}
                  request={item.request}
                  timelogs={item.timelogs}
                  isDetailed={showDetail}
                />
              );
            })}

            {!data?.length ? <Empty /> : undefined}
          </div>
        )}
      </div>
    </>
  ) : (
    <Box>
      <Empty description={<span className="text-gray-400">No project</span>} />
    </Box>
  );
};

export default UsagePage;
